/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useTranslation } from "react-i18next";

// components imports
import { Checkbox, Form, Select } from "antd";
// misc imports
import { IServiceTypeSettings, ISettings } from "@/interfaces/Service.interface";

const { Option } = Select;
type FieldType = Record<string, any>;

/**
 * Component to render form elements based on fetched project settings.
 *
 * @component
 * @param {Object} props - Component props
 * @param {IServiceTypeSettings[] | undefined} props.fetchedSettings - Fetched project settings
 * @returns {JSX.Element | null} Rendered form elements or null if `fetchedSettings` is undefined
 */
export const SettingsFormElements = ({ editable=true, settingsKey, settings }: { editable?: boolean; settingsKey: string; settings: IServiceTypeSettings[] }) => {
  const { t } = useTranslation();

  const getChoices = (setting: ISettings) => {
    // If there's no choices return an empty array
    if (!setting.choices) {
      return [];
    }
    // For any other selection just return the options as they are
    if (setting.value !== 'cache_type') {
      return setting.choices;
    }
    // For cache_type selector reverse the order the items appear in
    const reverseChoices: any[] = []
    for (let i = setting.choices.length - 1; i >= 0; i--) {
      reverseChoices.push(setting.choices[i])
    }
    return reverseChoices;
  }

  return settings.map((setting: ISettings) => {
    switch (setting.type) {
      // TODO: To be extended
      case "choice":
        // Render a Select element for choice type
        return (
          <Form.Item<FieldType>
            key={setting.value}
            label={setting.label}
            name={`${settingsKey}.${setting.value}`}
            rules={[
              {
                required: setting.required,
                message: t('validation.select', { label: setting.label.toLowerCase() })
              },
            ]}
            required={false}
            initialValue={setting.value !== 'cache_type' ?  setting.choices && setting.choices[0].value : null}
          >
            <Select disabled={!!(setting.choices && setting.choices.length < 2) || !editable} placeholder={t("common.select_option_placeholder")}>
              {getChoices(setting).map((choice) => (
                <Option key={choice.value} value={choice.value}>
                  {choice.display_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      case "boolean":
        // Render a Checkbox element for boolean type
        return (
          // Form.Item default bind value to value property, but Checkbox value property is checked. You can use valuePropName to change bind property.
          // source: https://ant.design/components/checkbox#why-not-work-in-formitem
          <Form.Item<FieldType> key={setting.value} label={setting.label} name={`${settingsKey}.${setting.value}`} valuePropName="checked">
            <Checkbox disabled={!editable} />
          </Form.Item>
        );
    }
  });
};
